import { useNavigate } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import { useDialog } from '@packages/ui/modal/use-dialog.tsx';
import SecondaryButton from '@packages/ui/button/secondary-button';
import { useDuplicateAppointmentMenuProduct } from '../logic/use-duplicate-appointment-menu-product';

interface ServiceMenuDuplicateProps {
  serviceId: string;
}

export default function ServiceMenuDuplicate({ serviceId }: ServiceMenuDuplicateProps) {
  const { t } = useTranslate();
  const { showAlert } = useNotificationCenter();
  const dialog = useDialog();
  const navigate = useNavigate();
  const [duplicateAppointmentMenuProduct, { loading }] = useDuplicateAppointmentMenuProduct();

  const handleDuplicate = () => {
    dialog.confirmAlert({
      title: t('service.service-detail.product.edit.extra-action.duplicate.confirm.title', 'Duplicate Service Product'),
      message: t(
        'service.service-detail.product.edit.extra-action.duplicate.confirm.message',
        'Are you sure you want to duplicate this service product?',
      ),
      textButton: t('service.service-detail.product.edit.extra-action.duplicate.confirm.button', 'Duplicate'),
      onConfirm: async () => {
        try {
          const result = await duplicateAppointmentMenuProduct({ variables: { id: serviceId } });
          showAlert({
            status: 'success',
            title: t('common.success'),
            message: t(
              'service.service-detail.product.edit.extra-action.duplicate.success',
              'Service product duplicated successfully!',
            ),
          });

          // Navigate to the newly created product
          const duplicatedProductId = result.data?.duplicateAppointmentMenuProduct.id;
          if (duplicatedProductId) {
            navigate(`../${duplicatedProductId}`);
          }
        } catch (error) {
          showAlert({
            status: 'error',
            title: t('common.error'),
            message: t(
              'service.service-detail.product.edit.extra-action.duplicate.error',
              'Failed to duplicate service product!',
            ),
          });
        }
      },
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-2">
          {t('service.service-detail.product.edit.extra-action.duplicate.title', 'Duplicate Product')}
        </h3>
        <p className="text-sm text-gray-600">
          {t(
            'service.service-detail.product.edit.extra-action.duplicate.description',
            'Create a copy of this service product with all its settings.',
          )}
        </p>
      </div>
      <SecondaryButton onClick={handleDuplicate} disabled={loading}>
        {t('service.service-detail.product.edit.extra-action.duplicate.button', 'Duplicate Product')}
      </SecondaryButton>
    </div>
  );
}
